// Import directly from the core implementation to avoid circular deps
import { defineStore } from '~/plugins/pinia-core'
import { defaultParams } from '@/utils/constants'

// We'll need these to access Nuxt's $axios and $auth
// Use module-level variables (not a best practice, but needed for testability)
// Make them accessible in the module exports for testing
// Using const with Object.defineProperty to satisfy ESLint while maintaining mutability
export const $axios = { current: null }
export const $auth = { current: null }

export const useStoredCardsStore = defineStore('storedCards', {
  state: () => ({
    storedCards: [],
  }),

  getters: {
    getCardById: (state) => (id) => {
      return state.storedCards.find((card) => card.id.toString() === id.toString())
    },
  },

  actions: {
    setStoredCards(storedCards) {
      this.storedCards = storedCards
    },

    async getAllStoredCards({
      withExpiredCards = true,
      paymentGatewayCodes = ['braintree_credit_cards', 'braintree_billing_agreements'],
    } = {}) {
      if (paymentGatewayCodes.length === 0) {
        this.setStoredCards([])
        return
      }

      // Use rootStore if available, otherwise try to use the global Vuex store during migration
      try {
        const { useRootStore } = require('~/stores/useRootStore')
        const rootStore = useRootStore()
        rootStore.addLoading('storedCards')
      } catch (error) {
        // Fallback to Vuex during migration
        if (window.$nuxt && window.$nuxt.$store) {
          window.$nuxt.$store.commit('ADD_LOADING', 'storedCards', { root: true })
        }
      }

      const params = {
        withExpiredCards,
        paymentGatewayCodes: paymentGatewayCodes.join(','),
        ...defaultParams,
      }

      try {
        const response = await $axios.current.get('/payments/cards', { params })
        const { data: storedCards } = response.data
        this.setStoredCards(storedCards)
      } catch (error) {
        console.error('Error getting stored cards:', error)
      } finally {
        // Remove loading state
        try {
          const { useRootStore } = require('~/stores/useRootStore')
          const rootStore = useRootStore()
          rootStore.removeLoading('storedCards')
        } catch (error) {
          // Fallback to Vuex during migration
          if (window.$nuxt && window.$nuxt.$store) {
            window.$nuxt.$store.commit('REMOVE_LOADING', 'storedCards', { root: true })
          }
        }
      }
    },

    async deleteStoredCard(id) {
      await $axios.current.delete(`/payments/cards/${id.toString()}`, { params: defaultParams })

      // Use setTimeout to match the original implementation's behavior
      setTimeout(() => {
        this.storedCards = this.storedCards.filter((card) => card.id.toString() !== id.toString())
      }, 200)
    },

    async makeDefaultPaymentMethod(cardId) {
      // Remove any existing errors
      try {
        const { useRootStore } = require('~/stores/useRootStore')
        const rootStore = useRootStore()
        rootStore.removeError('defaultPaymentMethod')
        rootStore.addLoading('storedCards')
      } catch (error) {
        // Fallback to Vuex during migration
        if (window.$nuxt && window.$nuxt.$store) {
          window.$nuxt.$store.commit('REMOVE_ERROR', 'defaultPaymentMethod', { root: true })
          window.$nuxt.$store.commit('ADD_LOADING', 'storedCards', { root: true })
        }
      }

      const params = { ...defaultParams }
      try {
        if (!$axios.current) {
          throw new Error('Axios is not initialized in storedCards store')
        }

        // Just make the API call without explicitly setting the Authorization header
        // The auth module should handle this automatically
        await $axios.current.put('/auth/user/defaultPaymentMethod', { defaultPaymentMethodId: cardId }, { params })
      } catch (error) {
        console.error('Error in makeDefaultPaymentMethod:', error)
        // Add error state
        try {
          const { useRootStore } = require('~/stores/useRootStore')
          const rootStore = useRootStore()
          rootStore.addError({ id: 'defaultPaymentMethod', error })
        } catch (err) {
          // Fallback to Vuex during migration
          if (window.$nuxt && window.$nuxt.$store) {
            window.$nuxt.$store.commit('ADD_ERROR', { id: 'defaultPaymentMethod', error }, { root: true })
          }
        }
        throw error
      } finally {
        // Remove loading state
        try {
          const { useRootStore } = require('~/stores/useRootStore')
          const rootStore = useRootStore()
          rootStore.removeLoading('storedCards')
        } catch (error) {
          // Fallback to Vuex during migration
          if (window.$nuxt && window.$nuxt.$store) {
            window.$nuxt.$store.commit('REMOVE_LOADING', 'storedCards', { root: true })
          }
        }
      }
    },
  },
})

// Initialization function to provide Nuxt's dependencies to the store
export function initializeStoredCardsStore(context) {
  $axios.current = context.$axios
  $auth.current = context.$auth
}
